import { makeStyles, type ColorPaletteName } from '@map/theme';
import { animated, useSpring } from '@react-spring/web';
import useMountEffect from '@shared/hooks/useMountEffect';
import Card from '@ui/mobile/Card';
import { useRef, type ComponentProps } from 'react';

const useStyles = makeStyles(
  ({ breakpoints, spacing, palette }, { color = 'primary' }: Pick<Props, 'color'>) => ({
    root: {
      flexDirection: 'row',
      justifyContent: 'center',
      '& > *:not(:first-child)': {
        position: 'relative',
      },
      [breakpoints.up('sm')]: {
        padding: spacing(6),
      },
    },
    timer: {
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      right: 0,
      transformOrigin: 'left center',
      backgroundColor: palette[color][100],
    },
  }),
);

type Props = {
  onTimeout: () => void;
  seconds: number;
  color?: ColorPaletteName;
} & ComponentProps<typeof Card>;

const TimeoutCta = ({ onTimeout, seconds, color, children, className, ...rest }: Props) => {
  const { classes, cx } = useStyles({ color });
  const canceledRef = useRef(false);

  const [styles, api] = useSpring(() => ({
    from: { transform: 'scaleX(0)', opacity: 1 },
    to: { transform: 'scaleX(1)', opacity: 1 },
    delay: 500,
    config: {
      duration: seconds * 1000 - 500,
    },
    onRest: () => {
      if (!canceledRef.current) {
        onTimeout();
      }
    },
  }));

  const handleCancel = () => {
    canceledRef.current = true;
    api.start({ to: { opacity: 0 }, config: { duration: undefined } });
  };

  useMountEffect(() => {
    window.addEventListener('keyup', handleCancel);
    return () => {
      window.removeEventListener('keyup', handleCancel);
    };
  });

  return (
    <Card className={cx(classes.root, className)} {...rest} onMouseMove={handleCancel}>
      <animated.div className={classes.timer} style={styles} />
      {children}
    </Card>
  );
};

export default TimeoutCta;
